import { memo, type ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { SUBMISSION_STATUSES } from '@didaskill/domain/assignments/constants';
import { OVERALL_AVERAGE_GRADING_SCALE } from '@didaskill/domain/courses/constants';
import { getTranscriptAssignmentsCount } from '@didaskill/domain/students/utils';
import type { StudentAcademicTranscript } from '@didaskill/domain/users/types';
import { Typography } from '@didaskill/ui/design-system';
import { academicTranscriptMessages, globalMessages, gradeMessages } from '@didaskill/ui/intl';

import * as styles from './AcademicTranscriptFooter.styles';

/**
 * Renders the `<Grade>` rich-text chunk of the overall average message.
 * @param chunks - Content wrapped by the `<Grade>` tag.
 * @returns The emphasized grade.
 */
const renderGrade = (chunks: ReactNode[]): ReactNode => (
  <Typography variant={Typography.VARIANTS.HEADING_3} element="span">
    {chunks}
  </Typography>
);

/**
 * Renders the `<GradingScale>` rich-text chunk as-is.
 * @param chunks - Content wrapped by the `<GradingScale>` tag.
 * @returns The unchanged chunks.
 */
const renderGradingScale = (chunks: ReactNode[]): ReactNode => chunks;

export const AcademicTranscriptFooterBase = ({
  transcript,
}: {
  transcript: StudentAcademicTranscript;
}) => {
  const overallGradedAssignments = useMemo(
    () => getTranscriptAssignmentsCount(transcript, [SUBMISSION_STATUSES.CORRECTED]),
    [transcript],
  );

  return (
    <div css={styles.footer}>
      <div css={styles.overallAverage}>
        <Typography variant={Typography.VARIANTS.HEADING_3} element="span">
          <FormattedMessage {...academicTranscriptMessages.OVERALL_AVERAGE} />
        </Typography>

        <Typography
          variant={Typography.VARIANTS.HEADING_5}
          element="span"
          css={styles.coloredTextPrimary}
        >
          <FormattedMessage
            {...(Number.isFinite(transcript.overallAverage)
              ? gradeMessages.GRADE
              : globalMessages.NOT_AFFECTED)}
            values={{
              grade: transcript.overallAverage,
              gradingScale: OVERALL_AVERAGE_GRADING_SCALE,
              Grade: renderGrade,
              GradingScale: renderGradingScale,
            }}
          />
        </Typography>
      </div>

      <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
        <FormattedMessage
          {...gradeMessages.GRADED_ASSIGNMENTS_COUNT}
          values={{
            ...overallGradedAssignments,
            b: (chunks) => (
              <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>{chunks}</Typography>
            ),
          }}
        />
      </Typography>
    </div>
  );
};

AcademicTranscriptFooterBase.displayName = 'AcademicTranscriptFooter';

export const AcademicTranscriptFooter = memo(AcademicTranscriptFooterBase);
